// leaderboardModel — the Match screen's leaderboard panel derivation (match-screen
// design D-match-1; client-match "Leaderboard model"). Pure over `MatchState`: no
// store read, no dispatch, no rendering — `Match.tsx` (group 6) renders what this
// module derives. Colony display names are not on the wire (D-074), so every row's
// label/colour comes from `colonyLabel`/`colonyPalette` (group 3), keyed solely by
// colony id — never a name, never per-render randomness.

import type { MatchState } from '../../net/wire/messages';
import { colonyLabel, colonyPalette } from './colony-visuals';

/** One leaderboard row. `livingCount` is `null` for an eliminated colony — the
 * caller renders that as an em-dash rather than a fabricated number (client-match
 * "the own row is marked and eliminated rows are dimmed with an em-dash"). */
export interface LeaderboardRow {
  colonyId: number;
  label: string;
  colour: string;
  isOwn: boolean;
  isEliminated: boolean;
  livingCount: number | null;
}

export interface LeaderboardModel {
  /** "N of M alive" — N = colonies not in `eliminations`, M = the distinct colony
   * count. The panel's static "Colonies · " prefix (UI.md §5) is the caller's. */
  header: string;
  /** Sorted: living rows by `livingCount` descending, then eliminated rows (in
   * their own last-known-`livingCount`-descending order), so the own row and every
   * colony's rank are always at a stable, predictable position. */
  rows: LeaderboardRow[];
}

function distinctColonyIds(matchState: MatchState): number[] {
  const seen = new Set<number>();
  for (const row of matchState.state.scoreboard) seen.add(row.colonyId);
  for (const id of matchState.state.eliminations) seen.add(id);
  return Array.from(seen);
}

/**
 * Derives the leaderboard from `matchState`. `colonyIds` is `manifest.colonyIds`
 * when the manifest has arrived, or `null` to fall back to `scoreboard ∪
 * eliminations` (client-match "the leaderboard shows N of M alive..." — the
 * manifest-absent branch). `ownColonyId` is `SessionSlice.colonyId`, or `null`
 * before it's resolved (no row is marked own).
 */
export function leaderboardModel(
  matchState: MatchState,
  ownColonyId: number | null,
  colonyIds: readonly number[] | null,
): LeaderboardModel {
  const eliminated = new Set(matchState.state.eliminations);
  const ids = colonyIds ?? distinctColonyIds(matchState);

  const total = ids.length;
  const alive = ids.filter((id) => !eliminated.has(id)).length;

  const entries = ids.map((id) => ({
    colonyId: id,
    isEliminated: eliminated.has(id),
    livingCount: matchState.state.scoreboard.find((r) => r.colonyId === id)?.livingCount ?? 0,
  }));

  entries.sort((a, b) => {
    if (a.isEliminated !== b.isEliminated) return a.isEliminated ? 1 : -1;
    if (b.livingCount !== a.livingCount) return b.livingCount - a.livingCount;
    return a.colonyId - b.colonyId;
  });

  const rows: LeaderboardRow[] = entries.map((e) => ({
    colonyId: e.colonyId,
    label: colonyLabel(e.colonyId, ownColonyId),
    colour: colonyPalette(e.colonyId),
    isOwn: ownColonyId != null && e.colonyId === ownColonyId,
    isEliminated: e.isEliminated,
    livingCount: e.isEliminated ? null : e.livingCount,
  }));

  return { header: `${alive} of ${total} alive`, rows };
}
